'use client';

import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExternalLink, Loader2, Search, Shield, Store } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { trpc } from '@/lib/api/trpc';
import { usePartnerAdmin } from '@/hooks/use-partner-admin';
import { useSuperAdmin } from '@/hooks/use-superadmin';
import type {
    AccountPlanPercentageColumn,
    AccountPlanPricesColumn,
} from '@/lib/types/accountPlans';
import type { StripeProductType } from '@/lib/stripe';

// ─── Column model ───────────────────────────────────────────────────
//
// One column per editable account_plans field. `money` fields are cents,
// `pct` fields are basis points (100 = 1.00%). `group` drives the subtle
// banding so adjacent $/% columns of the same fee read as one unit.

type ColKind = 'money' | 'pct';

interface PricingColumn {
    field: string;
    short: string;
    kind: ColKind;
    group: string;
    /** Required for percentage saves (updatePercentage takes a productType). */
    productType?: StripeProductType;
}

const COLUMNS: PricingColumn[] = [
    { field: 'amountPlan', short: 'Weekly base', kind: 'money', group: 'base' },
    { field: 'amountPerSuccessTransaction', short: 'Approved', kind: 'money', group: 'approved', productType: 'approved_transaction' },
    { field: 'pctPerSuccessTransaction', short: 'Approved %', kind: 'pct', group: 'approved', productType: 'approved_transaction' },
    { field: 'amountPerSubclubTransaction', short: 'SubClub', kind: 'money', group: 'subclub', productType: 'approved_subclub_transaction' },
    { field: 'pctPerSubclubTransaction', short: 'SubClub %', kind: 'pct', group: 'subclub', productType: 'approved_subclub_transaction' },
    { field: 'amountPerDeclinedTransaction', short: 'Declined', kind: 'money', group: 'declined', productType: 'declined_transaction' },
    { field: 'pctPerDeclinedTransaction', short: 'Declined %', kind: 'pct', group: 'declined', productType: 'declined_transaction' },
    { field: 'amountPerRefund', short: 'Refund', kind: 'money', group: 'refund', productType: 'refund_processing' },
    { field: 'pctPerRefund', short: 'Refund %', kind: 'pct', group: 'refund', productType: 'refund_processing' },
    { field: 'amountPer3dsVerification', short: '3DS', kind: 'money', group: '3ds', productType: '3ds_verification' },
    { field: 'pctPer3dsVerification', short: '3DS %', kind: 'pct', group: '3ds', productType: '3ds_verification' },
    { field: 'amountPerChargeBack', short: 'Chargeback', kind: 'money', group: 'chargeback', productType: 'chargeback_alert' },
    { field: 'pctPerChargeBack', short: 'Chargeback %', kind: 'pct', group: 'chargeback', productType: 'chargeback_alert' },
    { field: 'amountPerLatePayment', short: 'Late', kind: 'money', group: 'late', productType: 'late_payment' },
    { field: 'pctPerLatePayment', short: 'Late %', kind: 'pct', group: 'late', productType: 'late_payment' },
    { field: 'pctPerAutomaticRetry', short: 'Auto-retry %', kind: 'pct', group: 'retry', productType: 'automatic_retry' },
    { field: 'pctPerUpsell', short: 'Upsell %', kind: 'pct', group: 'upsell', productType: 'upsell' },
    { field: 'pctPerSubscription', short: 'Subscription %', kind: 'pct', group: 'subscription', productType: 'subscription' },
    { field: 'pctPerApplePay', short: 'Apple Pay %', kind: 'pct', group: 'applepay', productType: 'apple_pay' },
    { field: 'pctPerGooglePay', short: 'Google Pay %', kind: 'pct', group: 'googlepay', productType: 'google_pay' },
    { field: 'amountPerApplePayDomain', short: 'AP domain', kind: 'money', group: 'apdomain', productType: 'apple_pay_domain' },
    { field: 'amountPerCustomDomain', short: 'Custom domain', kind: 'money', group: 'customdomain', productType: 'custom_domain' },
];

function fmtMoney(cents: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2,
    }).format(cents / 100);
}

function fmtPct(bps: number): string {
    return `${(bps / 100).toFixed(2)}%`;
}

function fmtRaw(value: number | null, kind: ColKind, currency: string): string {
    if (value == null) return '—';
    return kind === 'money' ? fmtMoney(value, currency) : fmtPct(value);
}

// ─── Editable cell ──────────────────────────────────────────────────

interface EditableCellProps {
    value: number | null;
    kind: ColKind;
    currency: string;
    floor: number | null;
    disabled: boolean;
    saving: boolean;
    onCommit: (displayValue: number) => void;
}

/**
 * Click-to-edit numeric cell. Money is edited in the major unit (dollars),
 * percentages in percent; both convert to raw cents/bps as `× 100`. A value
 * below the buy-rate floor is refused locally (red ring) — the backend
 * enforces the same floor as defense-in-depth.
 */
function EditableCell({ value, kind, currency, floor, disabled, saving, onCommit }: EditableCellProps) {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState('');
    const [violated, setViolated] = React.useState(false);

    const toDisplay = (raw: number | null) => (raw == null ? '' : (raw / 100).toFixed(2));

    const start = () => {
        if (disabled) return;
        setViolated(false);
        setDraft(toDisplay(value));
        setEditing(true);
    };

    const commit = () => {
        const parsed = parseFloat(draft);
        if (Number.isNaN(parsed) || parsed < 0) {
            setEditing(false);
            return;
        }
        const raw = Math.round(parsed * 100);
        if (floor != null && raw < floor) {
            setViolated(true);
            toast.error(
                `Below the buy rate of ${kind === 'money' ? fmtMoney(floor, currency) : fmtPct(floor)}.`,
            );
            return;
        }
        // No-op when unchanged.
        if (value != null && raw === value) {
            setEditing(false);
            return;
        }
        onCommit(parsed);
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="flex items-center justify-end gap-1">
                {kind === 'money' && <span className="text-[11px] text-muted-foreground">$</span>}
                <Input
                    autoFocus
                    type="number"
                    step="0.01"
                    min={floor != null ? floor / 100 : 0}
                    value={draft}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        setViolated(false);
                    }}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') commit();
                        if (e.key === 'Escape') setEditing(false);
                    }}
                    className={`h-7 w-[72px] px-1.5 text-right text-xs tabular-nums ${
                        violated ? 'border-destructive ring-1 ring-destructive' : ''
                    }`}
                />
                {kind === 'pct' && <span className="text-[11px] text-muted-foreground">%</span>}
            </div>
        );
    }

    return (
        <button
            type="button"
            disabled={disabled}
            onClick={start}
            title={disabled ? undefined : 'Click to edit'}
            className={`flex w-full items-center justify-end gap-1 rounded px-1.5 py-1 text-right text-xs tabular-nums transition-colors ${
                disabled
                    ? 'cursor-default text-muted-foreground/50'
                    : 'hover:bg-muted cursor-text'
            }`}
        >
            {saving ? (
                <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
            ) : (
                <span className={value == null ? 'text-muted-foreground/50' : ''}>
                    {fmtRaw(value, kind, currency)}
                </span>
            )}
        </button>
    );
}

// ─── Page ───────────────────────────────────────────────────────────

export default function PartnerMerchantsPage() {
    const { partner: partnerParam } = useParams<{ partner: string }>();
    const slug = partnerParam ?? '';
    const { canView } = usePartnerAdmin(partnerParam);
    const { isSuperAdmin } = useSuperAdmin();
    const isPartnerSelf = canView && !isSuperAdmin;

    const adminQuery = trpc.partnerBuyRate.listMerchantPricing.useQuery(
        { partnerSlug: slug },
        { enabled: isSuperAdmin && slug.length > 0 },
    );
    const selfQuery = trpc.partnerBuyRate.selfListMerchantPricing.useQuery(
        { partner: slug },
        { enabled: isPartnerSelf && slug.length > 0 },
    );
    const query = isSuperAdmin ? adminQuery : selfQuery;

    const utils = trpc.useUtils();
    const invalidate = React.useCallback(() => {
        utils.partnerBuyRate.listMerchantPricing.invalidate();
        utils.partnerBuyRate.selfListMerchantPricing.invalidate();
    }, [utils]);

    // Track the cell currently committing, so only it shows a spinner.
    const [savingCell, setSavingCell] = React.useState<string | null>(null);
    const clearSaving = React.useCallback(() => setSavingCell(null), []);

    const updatePrice = trpc.accountManager.updatePrice.useMutation({
        onSuccess: () => { invalidate(); toast.success('Price updated'); },
        onError: (e) => toast.error(e.message || 'Failed to update'),
        onSettled: clearSaving,
    });
    const updatePercentage = trpc.accountManager.updatePercentage.useMutation({
        onSuccess: () => { invalidate(); toast.success('Percentage updated'); },
        onError: (e) => toast.error(e.message || 'Failed to update'),
        onSettled: clearSaving,
    });
    const updateWeekly = trpc.accountManager.updateWeeklyAmount.useMutation({
        onSuccess: () => { invalidate(); toast.success('Weekly base updated'); },
        onError: (e) => toast.error(e.message || 'Failed to update'),
        onSettled: clearSaving,
    });

    const [search, setSearch] = React.useState('');

    if (!canView) {
        return (
            <div className="flex min-h-[40vh] items-center justify-center">
                <div className="text-center">
                    <Shield className="mx-auto h-12 w-12 text-muted-foreground" />
                    <h2 className="mt-4 text-xl font-semibold">Access Denied</h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        You don't have access to merchant pricing for this partner.
                    </p>
                </div>
            </div>
        );
    }

    const data = query.data;
    const floor = data?.floor ?? null;
    const merchants = (data?.merchants ?? []).filter((m) =>
        m.accountName.toLowerCase().includes(search.trim().toLowerCase()),
    );

    const commitCell = (
        accountId: string,
        col: PricingColumn,
        displayValue: number,
    ) => {
        const cellKey = `${accountId}:${col.field}`;
        setSavingCell(cellKey);
        if (col.field === 'amountPlan') {
            updateWeekly.mutate({ accountId, amount: Math.round(displayValue * 100) });
        } else if (col.kind === 'money') {
            updatePrice.mutate({
                accountId,
                priceType: col.field as AccountPlanPricesColumn,
                amount: Math.round(displayValue * 100),
            });
        } else {
            updatePercentage.mutate({
                accountId,
                productType: col.productType!,
                percentage: displayValue,
            });
        }
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Store className="h-5 w-5" />
                                Merchant Pricing
                            </CardTitle>
                            <CardDescription>
                                Every merchant's sell rate in one grid. Click any cell to edit.
                                {floor
                                    ? ' Each column shows your buy rate (min) — you can price above it, never below.'
                                    : ' No signed buy rate yet, so there is no floor.'}
                            </CardDescription>
                        </div>
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search merchant…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="pl-8"
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {query.isLoading ? (
                        <div className="space-y-2">
                            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-9 w-full" />)}
                        </div>
                    ) : query.error ? (
                        <p className="py-8 text-center text-sm text-destructive">{query.error.message}</p>
                    ) : merchants.length === 0 ? (
                        <p className="py-10 text-center text-sm text-muted-foreground">
                            {search ? 'No merchant matches your search.' : 'No merchants under this partner yet.'}
                        </p>
                    ) : (
                        <div className="overflow-x-auto rounded-md border">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/40">
                                        <th className="sticky left-0 z-20 min-w-[200px] bg-muted/40 px-3 py-2 text-left font-medium">
                                            Merchant
                                        </th>
                                        {COLUMNS.map((col, i) => {
                                            const floorRaw =
                                                floor && typeof (floor as any)[col.field] === 'number'
                                                    ? ((floor as any)[col.field] as number)
                                                    : null;
                                            const showFloor = floorRaw != null && floorRaw > 0;
                                            const groupStart = i === 0 || COLUMNS[i - 1].group !== col.group;
                                            return (
                                                <th
                                                    key={col.field}
                                                    className={`whitespace-nowrap px-2 py-2 text-right align-bottom font-medium ${
                                                        groupStart ? 'border-l' : ''
                                                    }`}
                                                >
                                                    <div className="leading-tight">{col.short}</div>
                                                    <div className="h-3 text-[10px] font-normal leading-none text-muted-foreground/60">
                                                        {showFloor
                                                            ? `min ${col.kind === 'money' ? fmtMoney(floorRaw!, 'usd') : fmtPct(floorRaw!)}`
                                                            : ''}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {merchants.map((m) => (
                                        <tr key={m.accountId} className="border-b last:border-0 hover:bg-muted/20">
                                            <td className="sticky left-0 z-10 min-w-[200px] bg-background px-3 py-1.5 hover:bg-muted/20">
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="truncate font-medium" title={m.accountName}>
                                                        {m.accountName}
                                                    </span>
                                                    {m.orgSlug && (
                                                        <Link
                                                            to={`/org/${m.orgSlug}/account-manager`}
                                                            title="Open full pricing tab"
                                                            className="text-muted-foreground hover:text-foreground"
                                                        >
                                                            <ExternalLink className="h-3.5 w-3.5" />
                                                        </Link>
                                                    )}
                                                </div>
                                            </td>
                                            {COLUMNS.map((col, i) => {
                                                const floorRaw =
                                                    floor && typeof (floor as any)[col.field] === 'number'
                                                        ? ((floor as any)[col.field] as number)
                                                        : null;
                                                const groupStart = i === 0 || COLUMNS[i - 1].group !== col.group;
                                                const cellKey = `${m.accountId}:${col.field}`;
                                                return (
                                                    <td
                                                        key={col.field}
                                                        className={`px-1 py-1 ${groupStart ? 'border-l' : ''}`}
                                                    >
                                                        <EditableCell
                                                            value={m.fees[col.field as keyof typeof m.fees] ?? null}
                                                            kind={col.kind}
                                                            currency={m.billingCurrency}
                                                            floor={floorRaw}
                                                            disabled={!m.hasPlan}
                                                            saving={savingCell === cellKey}
                                                            onCommit={(v) => commitCell(m.accountId, col, v)}
                                                        />
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
